import { useCallback, useMemo } from 'react'

import { useApolloClient } from '@apollo/client'
import type { InFilterDefinition } from '@factorialco/f0-react'
import {
  EmployeeWorkplacesFilterDocument,
  FeatureFlagAvailabilitiesDocument,
} from 'generated/resources/graphql'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import i18n from 'modules/core/lib/i18n'

const useWorkplaceBaseFilter = ({
  includeWorkAreas,
}: {
  includeWorkAreas: boolean
}): InFilterDefinition<string> => {
  const client = useApolloClient()
  const { company } = useCurrentSession()

  const loadOptions = useCallback(async () => {
    let shouldIncludeWorkAreas = false

    if (includeWorkAreas) {
      const { data: featureData } = await client.query({
        query: FeatureFlagAvailabilitiesDocument,
        variables: {
          companyId: company.id,
          featureNames: ['dev_employee_location_work_area'],
        },
        fetchPolicy: 'cache-first',
      })

      shouldIncludeWorkAreas =
        featureData?.features?.featuresConnection.nodes.some(
          (feature) => feature.name === 'dev_employee_location_work_area' && feature.enabled
        ) ?? false
    }

    const { data } = await client.query({
      query: EmployeeWorkplacesFilterDocument,
      variables: {
        includeWorkAreas: shouldIncludeWorkAreas,
      },
      fetchPolicy: 'cache-first',
    })

    return data.locations.locations.map((location) => {
      const workAreas = location.workAreas ?? []

      const children =
        shouldIncludeWorkAreas && workAreas.length > 0
          ? {
              filterKey: 'workArea',
              options: workAreas.map((workArea) => ({
                label: workArea.name,
                value: workArea.id,
              })),
            }
          : undefined

      return {
        label: location.name,
        value: location.id,
        ...(children ? { children } : {}),
      }
    })
  }, [client, company.id, includeWorkAreas])

  return useMemo(
    () => ({
      label: i18n.t('employees.employees_list.headers.workplace'),
      type: 'in' as const,
      options: {
        cache: true,
        options: loadOptions,
      },
    }),
    [loadOptions]
  )
}

export const useWorkplaceFilter = ({
  withWorkAreas = false,
}: {
  withWorkAreas?: boolean
} = {}): InFilterDefinition<string> => {
  return useWorkplaceBaseFilter({ includeWorkAreas: withWorkAreas })
}

export const useWorkplaceWithWorkAreasFilter = (): InFilterDefinition<string> => {
  return useWorkplaceFilter({ withWorkAreas: true })
}
